/**
 * Divisor — nativo.
 *
 * ## Por que saiu da ponte
 *
 * Três pedidos que o widget legado não atendia, e os três pelo mesmo motivo:
 *
 *  - **o vertical não acompanhava a altura.** O `nz-divider` vertical do
 *    NG-Zorro desenha uma risca de `1em` — altura de uma linha de texto, fixa.
 *    Ele não cresce com o componente ao lado nem com a altura pedida no layout,
 *    e não há propriedade para isso;
 *  - **o estilo da linha era fixo.** A ponte cravava `variant: 'solid'`, então
 *    tracejado e pontilhado não existiam;
 *  - **a cor não existia.** O widget lia uma custom property que ninguém
 *    preenchia.
 *
 * Aqui é uma borda de CSS, e as três saem de graça: `border-style` dá o traço,
 * `border-color` sai de token, e `height: 100%` faz o vertical acompanhar o que
 * o layout reservou.
 *
 * **A cor é semântica, não livre.** Não há seletor de hexadecimal: o usuário
 * escolhe o PAPEL (neutro, destaque, atenção…) e o tema escolhe a cor. É a
 * mesma regra do resto do motor, e é o que faz o divisor continuar legível nos
 * oito temas — inclusive no personalizado, que o usuário edita cor a cor.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { ScreenNode } from '../../model/node-props';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { dividerColorToken, dividerLineStyle } from './divider-style';

@Component({
  selector: 'tem-divider-field',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.tem-divider-host--vertical]': 'isVertical()' },
  template: `
    @if (isVertical()) {
      <!-- Vertical: acompanha a altura que o layout reservou. O nz-divider
           desenhava 1em fixo, e era isso que não crescia. -->
      <div
        class="tem-divider tem-divider--vertical"
        [style.border-left-style]="lineStyle()"
        [style.border-left-color]="color()"></div>

    } @else if (title(); as text) {
      <!-- Com título: a linha vem dos dois lados, e o alinhamento decide quanto
           de cada lado. Uma grade de três colunas resolve os três casos sem
           if no meio do template. -->
      <div class="tem-divider tem-divider--titled" [ngClass]="alignClass()">
        <span
          class="tem-divider__line"
          [style.border-top-style]="lineStyle()"
          [style.border-top-color]="color()"></span>

        <span class="tem-divider__text">{{ text }}</span>

        <span
          class="tem-divider__line"
          [style.border-top-style]="lineStyle()"
          [style.border-top-color]="color()"></span>
      </div>

    } @else {
      <div
        class="tem-divider tem-divider--plain"
        [style.border-top-style]="lineStyle()"
        [style.border-top-color]="color()"></div>
    }
  `,
  styles: [`
    /* A altura de um campo de texto COM rotulo: rotulo (1,25rem) + o espaco de
       4px entre ele e a caixa + a caixa (2rem). Sem isso, um divisor ao lado de
       um campo desenha a risca colada no topo da celula, e nao no meio dela.

       min-height, e nao height: quando o layout pede mais linhas, ou quando
       o titulo quebra, ele cresce. */
    :host {
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-height: calc(1.25rem + var(--tem-space-1) + 2rem);
    }

    /* Vertical nao reserva essa faixa: a altura dele vem do vizinho. */
    :host(.tem-divider-host--vertical) { min-height: 1.5rem; }

    /* Sem margem propria: o espaco em volta agora vem da altura reservada no
       host, e somar os dois daria um divisor mais alto que o vizinho. */
    .tem-divider--plain {
      border-top-width: 1px;
    }

    /* A altura vem de fora: 100% do que o layout reservou. Sem isto ele
       desenharia a altura do conteudo, que e zero. */
    .tem-divider--vertical {
      border-left-width: 1px;

      /* Cresce ate a altura que o involucro tiver — que e a da linha da grade
         quando ha vizinho, e a das linhas pedidas quando o layout pede altura.
         Nada de porcentagem: height: 100% exigiria altura definida no pai, e
         a cadeia toda teria de estar certa para a risca aparecer. */
      flex: 1 1 auto;
      min-height: 1.5rem;
      margin: 0 auto;
      width: 0;
    }

    .tem-divider--titled {
      display: grid;
      align-items: center;
      gap: var(--tem-space-2);
    }

    /* O alinhamento do titulo e a PROPORCAO das duas linhas. A da esquerda
       encurta quando o texto vai para a esquerda, e vice-versa. */
    .tem-divider--align-center { grid-template-columns: 1fr auto 1fr; }
    .tem-divider--align-left   { grid-template-columns: var(--tem-space-4) auto 1fr; }
    .tem-divider--align-right  { grid-template-columns: 1fr auto var(--tem-space-4); }

    .tem-divider__line { border-top-width: 1px; }

    .tem-divider__text {
      color: var(--tem-muted);
      font-size: var(--tem-fs-sm);
      white-space: nowrap;
    }
  `]
})
export class DividerFieldRenderer implements NodeRenderer<'divider'> {

  readonly node = input.required<ScreenNode<'divider'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly translate = inject(TranslateService);

  private readonly props = computed(() => this.node().props);

  readonly isVertical = computed<boolean>(() => this.props().orientation === 'vertical');

  readonly title = computed<string>(() => {
    const chave = this.props().titleKey;

    return typeof chave === 'string' && chave.length > 0
      ? translateTAValue(this.translate, chave)
      : '';
  });

  readonly lineStyle = computed<string>(() => dividerLineStyle(this.props()));

  readonly color = computed<string>(() => dividerColorToken(this.props()));

  readonly alignClass = computed<string>(
    () => 'tem-divider--align-' + (this.props().titleAlign ?? 'center')
  );
}
